import { doc, getDoc, setDoc, onSnapshot, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getCompatibleVehicleTypes } from '../config/vehicleTypes';

class WaitTimeManager {
  constructor() {
    this.isActive = false;
    this.updateInterval = null;
    this.firebaseListener = null;
    this.shiftsListener = null;
    this.lastCalculationTime = null;
    this.currentWaitTimes = {
      general: "Berechnung...",
      grossraum: "Berechnung...",
      kleinbus7: "Berechnung...",
      kleinbus8: "Berechnung..."
    };
    this.nextUnavailable = null;
    this.waitTimeMetadata = null;
    
    // Previous values for change detection
    this.previousWaitTimes = null;
    this.previousNextUnavailable = null;
    
    // Callbacks für UI-Updates
    this.updateCallbacks = new Set();
    
    // Data dependencies
    this.shiftsData = null;
    this.activities = [];
    this.vehicles = [];
    this.currentDate = new Date();
    
    // Firebase shift data loading (nur initial, keine Listener)
    this.loadShiftsData = this.loadShiftsData.bind(this);
    
    this.roundToFiveMinutes = this.roundToFiveMinutes.bind(this);
    this.getActiveShift = this.getActiveShift.bind(this);
    this.calculateTimeToNextUnavailability = this.calculateTimeToNextUnavailability.bind(this);
    this.findNextAvailableSegment = this.findNextAvailableSegment.bind(this);
    this.calculateWaitTimeFromTimeline = this.calculateWaitTimeFromTimeline.bind(this);
    this.calculateCurrentWaitTime = this.calculateCurrentWaitTime.bind(this);
    this.calculateVehicleTypeAvailability = this.calculateVehicleTypeAvailability.bind(this);
    this.buildAvailabilityTimeline = this.buildAvailabilityTimeline.bind(this);
    this.getTimeSlots = this.getTimeSlots.bind(this);
  }

  // Hilfsfunktion für aktive Schicht im neuen Array-System
  getActiveShift(driverData) {
    if (!driverData || !driverData.shifts || !Array.isArray(driverData.shifts)) {
      return null;
    }
    return driverData.shifts.find(shift => shift.isActive) || null;
  }

  // 5-Minuten-Aufrundung
  roundToFiveMinutes(minutes) {
    return Math.ceil(minutes / 5) * 5;
  }

  // Zeitslots generieren basierend auf aktuellem Datum
  getTimeSlots() {
    const slots = [];
    const now = new Date();
    
    // Rolling Window: Von aktueller Zeit bis 30h später (1.5 Tage)
    // Das deckt Pausen über Mitternacht und Schichten des nächsten Tages ab
    
    // Startzeitpunkt: Aktueller Tag 00:00
    const startDate = new Date(now);
    startDate.setHours(0, 0, 0, 0);
    
    // Endzeitpunkt: Nächster Tag 06:00 (30 Stunden später)
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 1);
    endDate.setHours(6, 0, 0, 0);
    
    // Stundenbasierte Slots für erweiterten Zeitraum
    let currentSlotTime = new Date(startDate);
    
    while (currentSlotTime < endDate) {
      slots.push({
        date: new Date(currentSlotTime),
        displayHour: currentSlotTime.getHours(),
        actualTime: new Date(currentSlotTime)
      });
      
      // Nächste Stunde
      currentSlotTime.setHours(currentSlotTime.getHours() + 1);
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`WaitTimeManager: Timeline erweitert von ${startDate.toLocaleString()} bis ${endDate.toLocaleString()} (${slots.length} Stunden)`);
    }
    
    return slots;
  }

  // Timeline für Verfügbarkeit erstellen
  buildAvailabilityTimeline() {
    const timeSlots = this.getTimeSlots();
    if (timeSlots.length === 0) return { segments: [] };
    
    const firstSlot = timeSlots[0];
    const lastSlot = timeSlots[timeSlots.length - 1];
    
    // Verwende die actualTime aus den erweiterten Slots
    const baseTime = firstSlot.actualTime || new Date(firstSlot.date);
    const endTime = lastSlot.actualTime || new Date(lastSlot.date);
    
    // Korrigiere Endzeit (letzte Stunde + 1 für komplette Abdeckung)
    const correctedEndTime = new Date(endTime);
    correctedEndTime.setHours(correctedEndTime.getHours() + 1);
    
    const totalMinutes = (correctedEndTime - baseTime) / (1000 * 60);
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`WaitTimeManager: Timeline ${baseTime.toLocaleString()} - ${correctedEndTime.toLocaleString()} (${totalMinutes} Minuten)`);
    }
    
    // Minütliche Timeline-Erstellung
    const timeline = [];
    
    for (let minute = 0; minute < totalMinutes; minute++) {
      const checkTime = new Date(baseTime.getTime() + minute * 60000);
      
      let availableCount = 0;
      let totalActiveDrivers = 0;
      let availableWithinShiftHours = 0;
      let availableOverShiftHours = 0;
      
      // Für jeden Fahrer prüfen
      if (this.shiftsData && this.shiftsData.drivers) {
        Object.entries(this.shiftsData.drivers).forEach(([driverId, driverData]) => {
          const currentShift = this.getActiveShift(driverData);
          if (!currentShift) return;
          
          // Schichtzeiten und Status prüfen
          const shiftStart = currentShift.shiftStart?.toDate();
          const shiftEnd = currentShift.shiftEnd?.toDate();
          const isShiftActive = currentShift.isActive;
          
          if (!isShiftActive || !shiftStart || checkTime < shiftStart) return;
          if (shiftEnd && checkTime > shiftEnd) return;
          
          totalActiveDrivers++;
          
          // 8-Stunden-Grenze berechnen
          const maxShiftTime = new Date(shiftStart.getTime() + (8 * 60 * 60 * 1000));
          
          // Pausenprüfung
          const isInBreak = currentShift.breaks && currentShift.breaks.some(breakItem => {
            if (!breakItem.startTime) return false;
            const breakStart = breakItem.startTime.toDate();
            const breakEnd = breakItem.endTime ? breakItem.endTime.toDate() : 
              new Date(breakStart.getTime() + ((breakItem.plannedDuration || 30) * 60000));
            return breakStart <= checkTime && breakEnd > checkTime;
          });
          
          // Fahrtprüfung mit erweiterten Debug-Logs
          const isOnRide = this.activities.some(activity => {
            if (activity.type !== 'ride') return false;
            if (activity.driverId !== driverId) return false;
            
            const blockingStatuses = ['pre_accepted', 'accepted', 'arrived', 'in_progress'];
            if (!blockingStatuses.includes(activity.status)) return false;
            
            const effectiveStartTime = (activity.status === 'pre_accepted' && activity.hasApproachTime) ? 
              activity.approachStartTime : activity.startTime;
            
            return effectiveStartTime <= checkTime && activity.endTime > checkTime;
          });
          
          if (!isInBreak && !isOnRide) {
            availableCount++;
            
            if (checkTime <= maxShiftTime) {
              availableWithinShiftHours++;
            } else {
              availableOverShiftHours++;
            }
          }
        });
      }
      
      const totalAvailable = availableWithinShiftHours + availableOverShiftHours;
      
      timeline.push({
        minute,
        time: checkTime,
        available: totalAvailable > 0,
        availableCount: totalAvailable,
        availableWithinShiftHours,
        availableOverShiftHours,
        totalActiveDrivers,
        status: availableWithinShiftHours > 0 ? 'normal' : (availableOverShiftHours > 0 ? 'overtime' : 'unavailable')
      });
    }
    
    // Segment-Gruppierung
    const segments = [];
    let currentSegment = null;
    
    timeline.forEach((point) => {
      if (!currentSegment || currentSegment.status !== point.status) {
        if (currentSegment) {
          segments.push(currentSegment);
        }
        currentSegment = {
          startMinute: point.minute,
          endMinute: point.minute + 1,
          available: point.available,
          availableCount: point.availableCount,
          availableWithinShiftHours: point.availableWithinShiftHours,
          availableOverShiftHours: point.availableOverShiftHours,
          totalWithDriver: point.totalActiveDrivers,
          status: point.status,
          startTime: point.time,
          endTime: new Date(point.time.getTime() + 60000)
        };
      } else {
        currentSegment.endMinute = point.minute + 1;
        currentSegment.endTime = new Date(point.time.getTime() + 60000);
        currentSegment.availableCount = point.availableCount;
        currentSegment.availableWithinShiftHours = point.availableWithinShiftHours;
        currentSegment.availableOverShiftHours = point.availableOverShiftHours;
        currentSegment.totalWithDriver = point.totalActiveDrivers;
      }
    });
    
    if (currentSegment) {
      segments.push(currentSegment);
    }
    
    return { segments };
  }

  // Zeit bis zur nächsten Nicht-Verfügbarkeit berechnen
  calculateTimeToNextUnavailability() {
    const now = new Date();
    const { segments } = this.buildAvailabilityTimeline();
    
    if (!segments || segments.length === 0) return null;
    
    // Finde das nächste unavailable Segment nach der aktuellen Zeit
    const nextUnavailableSegment = segments.find(segment => 
      segment.status === 'unavailable' && segment.startTime > now
    );
    
    if (!nextUnavailableSegment) return null;
    
    const minutesUntilUnavailable = Math.ceil((nextUnavailableSegment.startTime - now) / (1000 * 60));
    
    return {
      minutesUntil: minutesUntilUnavailable,
      startTime: nextUnavailableSegment.startTime,
      endTime: nextUnavailableSegment.endTime,
      duration: Math.ceil((nextUnavailableSegment.endTime - nextUnavailableSegment.startTime) / (1000 * 60))
    };
  }

  // Nächstes verfügbares Segment finden
  findNextAvailableSegment() {
    const now = new Date();
    const { segments } = this.buildAvailabilityTimeline();
    
    if (!segments || segments.length === 0) {
      if (process.env.NODE_ENV === 'development') {
        console.log('WaitTimeManager: Keine Segmente gefunden für nextAvailable-Suche');
      }
      return null;
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`WaitTimeManager: Suche nextAvailable ab ${now.toLocaleTimeString()} in ${segments.length} Segmenten`);
      console.log('Verfügbare Segmente:', segments.map(s => `${s.startTime.toLocaleTimeString()}-${s.endTime.toLocaleTimeString()} (${s.status})`));
    }
    
    // Finde das nächste verfügbare Segment (normal oder overtime) nach der aktuellen Zeit
    const nextAvailableSegment = segments.find(segment => 
      (segment.status === 'normal' || segment.status === 'overtime') && 
      segment.startTime > now
    );
    
    if (!nextAvailableSegment) {
      if (process.env.NODE_ENV === 'development') {
        console.log('WaitTimeManager: Kein verfügbares Segment nach aktueller Zeit gefunden');
      }
      return null;
    }
    
    const minutesUntilAvailable = Math.ceil((nextAvailableSegment.startTime - now) / (1000 * 60));
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`WaitTimeManager: Nächstes verfügbares Segment in ${minutesUntilAvailable} Min um ${nextAvailableSegment.startTime.toLocaleTimeString()}`);
    }
    
    return {
      minutesUntil: minutesUntilAvailable,
      startTime: nextAvailableSegment.startTime,
      availableCount: nextAvailableSegment.availableCount,
      status: nextAvailableSegment.status
    };
  }

  // Fahrzeugtyp-spezifische Version: Zeit bis zur nächsten Nicht-Verfügbarkeit aus gegebenen Segmenten
  calculateTimeToNextUnavailabilityFromSegments(segments) {
    const now = new Date();
    
    if (!segments || segments.length === 0) return null;
    
    // Finde das nächste unavailable Segment nach der aktuellen Zeit
    const nextUnavailableSegment = segments.find(segment => 
      segment.status === 'unavailable' && segment.startTime > now
    );
    
    if (!nextUnavailableSegment) return null;
    
    const minutesUntilUnavailable = Math.ceil((nextUnavailableSegment.startTime - now) / (1000 * 60));
    
    return {
      minutesUntil: minutesUntilUnavailable,
      startTime: nextUnavailableSegment.startTime,
      endTime: nextUnavailableSegment.endTime,
      duration: Math.ceil((nextUnavailableSegment.endTime - nextUnavailableSegment.startTime) / (1000 * 60)),
      status: nextUnavailableSegment.status
    };
  }

  // Fahrzeugtyp-spezifische Version: Finde nächstes verfügbares Segment aus gegebenen Segmenten
  findNextAvailableSegmentFromSegments(segments) {
    const now = new Date();
    
    if (!segments || segments.length === 0) {
      if (process.env.NODE_ENV === 'development') {
        console.log('WaitTimeManager: Keine Segmente für fahrzeugspezifische nextAvailable-Suche');
      }
      return null;
    }
    
    // Finde das nächste verfügbare Segment (normal oder unavailable mit availableCount > 0) nach der aktuellen Zeit
    const nextAvailableSegment = segments.find(segment => 
      segment.availableCount > 0 && segment.startTime > now
    );
    
    if (!nextAvailableSegment) {
      if (process.env.NODE_ENV === 'development') {
        console.log('WaitTimeManager: Kein verfügbares fahrzeugspezifisches Segment nach aktueller Zeit gefunden');
      }
      return null;
    }
    
    const minutesUntilAvailable = Math.ceil((nextAvailableSegment.startTime - now) / (1000 * 60));
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`WaitTimeManager: Nächstes fahrzeugspezifisches Segment in ${minutesUntilAvailable} Min um ${nextAvailableSegment.startTime.toLocaleTimeString()}`);
    }
    
    return {
      minutesUntil: minutesUntilAvailable,
      startTime: nextAvailableSegment.startTime,
      availableCount: nextAvailableSegment.availableCount,
      status: nextAvailableSegment.status
    };
  }

  // Wartezeit basierend auf Timeline-Daten berechnen
  calculateWaitTimeFromTimeline(currentAvailable, vehicleTypeSegments = null) {
    // Prüfe zuerst ob überhaupt Fahrer angemeldet sind (ohne shiftEnd)
    const totalDriversRegistered = this.shiftsData?.drivers 
      ? Object.values(this.shiftsData.drivers).filter(driver => !driver.shiftEnd).length 
      : 0;
      
    if (totalDriversRegistered === 0) {
      return "Keine Fahrer verfügbar";
    }
    
    if (currentAvailable > 0) {
      // Fahrer aktuell verfügbar
      const nextUnavailable = vehicleTypeSegments 
        ? this.calculateTimeToNextUnavailabilityFromSegments(vehicleTypeSegments)
        : this.calculateTimeToNextUnavailability();
      
      if (!nextUnavailable || nextUnavailable.minutesUntil > 20) {
        // Standard-Wartezeit wenn keine baldige Nicht-Verfügbarkeit
        return "5-10 Min";
      } else {
        // Kurz vor rotem Abschnitt - warte bis nach Ausfallzeit
        const totalWaitTime = nextUnavailable.minutesUntil + nextUnavailable.duration;
        const roundedTime = this.roundToFiveMinutes(totalWaitTime);
        return `${roundedTime}-${roundedTime + 5} Min (nach Ausfallzeit)`;
      }
    } else {
      // Keine Fahrer verfügbar - finde nächsten verfügbaren Zeitpunkt
      const nextAvailable = vehicleTypeSegments 
        ? this.findNextAvailableSegmentFromSegments(vehicleTypeSegments)
        : this.findNextAvailableSegment();
      
      if (!nextAvailable) {
        // Prüfe ob überhaupt Fahrer online sind
        const hasAnyOnlineDriver = this.shiftsData?.drivers && 
          Object.values(this.shiftsData.drivers).some(driver => 
            driver.shifts?.some(shift => shift.isActive)
          );
        return hasAnyOnlineDriver ? "Derzeit alle Fahrer beschäftigt" : "Keine Fahrer verfügbar";
      }
      
      const roundedTime = this.roundToFiveMinutes(nextAvailable.minutesUntil);
      return `${roundedTime}-${roundedTime + 5} Min`;
    }
  }

  // Fahrzeugtyp-spezifische Verfügbarkeit berechnen
  calculateVehicleTypeAvailability(vehicleType) {
    if (process.env.NODE_ENV === 'development') {
      console.log(`WaitTimeManager: 🔍 KOMBI DEBUG - Starte calculateVehicleTypeAvailability für Typ ${vehicleType}`);
      console.log('WaitTimeManager: Verfügbare Fahrzeuge:', this.vehicles.length);
      console.log('WaitTimeManager: Fahrzeuge Details:', this.vehicles.map(v => ({
        id: v.id, 
        type: v.vehicleType, 
        hasDriver: !!v.current_driver,
        driverId: v.current_driver?.id
      })));
    }
    
    const timeSlots = this.getTimeSlots();
    if (timeSlots.length === 0) return { segments: [] };
    
    const firstSlot = timeSlots[0];
    const lastSlot = timeSlots[timeSlots.length - 1];
    
    // Verwende die actualTime aus den erweiterten Slots (wie in buildAvailabilityTimeline)
    const baseTime = firstSlot.actualTime || new Date(firstSlot.date);
    const endTime = lastSlot.actualTime || new Date(lastSlot.date);
    
    // Korrigiere Endzeit (letzte Stunde + 1 für komplette Abdeckung)
    const correctedEndTime = new Date(endTime);
    correctedEndTime.setHours(correctedEndTime.getHours() + 1);
    
    const totalMinutes = (correctedEndTime - baseTime) / (1000 * 60);
    
    // Filtere Fahrzeuge nach Typ (Kompatibilitätsmatrix: größere Fahrzeuge können kleinere Bestellungen bedienen)
    const compatibleTypes = getCompatibleVehicleTypes(vehicleType);
    const vehiclesOfType = this.vehicles.filter(v => compatibleTypes.includes(v.vehicleType));
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`WaitTimeManager: 🔍 KOMBI DEBUG - Gefilterte Fahrzeuge für Typ ${vehicleType}:`, vehiclesOfType.length);
      vehiclesOfType.forEach(v => {
        console.log(`- Fahrzeug ${v.id}: Typ ${v.vehicleType}, Fahrer: ${v.current_driver?.id || 'keiner'}`);
      });
    }
    
    const timeline = [];
    
    for (let minute = 0; minute < totalMinutes; minute++) {
      const checkTime = new Date(baseTime.getTime() + minute * 60000);
      
      let availableCount = 0;
      let totalWithDriver = 0;
      
      vehiclesOfType.forEach(vehicle => {
        // Debug für erstes Minute bei Kombi
        if (process.env.NODE_ENV === 'development' && vehicleType === 2 && minute === 0) {
          console.log(`WaitTimeManager: 🔍 KOMBI DEBUG - Prüfe Fahrzeug ${vehicle.id} (Typ ${vehicle.vehicleType})`);
          console.log(`- Hat Fahrer: ${!!vehicle.current_driver}`);
          if (vehicle.current_driver) {
            console.log(`- Fahrer ID: ${vehicle.current_driver.id}`);
          }
        }
        
        // Prüfe ob das Fahrzeug einen aktiven Fahrer hat
        if (!vehicle.current_driver) {
          if (process.env.NODE_ENV === 'development' && vehicleType === 2 && minute === 0) {
            console.log(`- Fahrzeug ${vehicle.id}: Kein Fahrer zugewiesen`);
          }
          return;
        }
        
        const driverId = vehicle.current_driver.id;
        
        if (process.env.NODE_ENV === 'development' && vehicleType === 2 && minute === 0) {
          console.log(`- Fahrzeug ${vehicle.id}: Suche Schichtdaten für Fahrer ${driverId}`);
          console.log(`- Shifts Data verfügbar: ${!!this.shiftsData}`);
          console.log(`- Driver in Shifts: ${!!(this.shiftsData?.drivers?.[driverId])}`);
        }
        
        // Prüfe aktive Schicht
        if (this.shiftsData && this.shiftsData.drivers && this.shiftsData.drivers[driverId]) {
          const driverData = this.shiftsData.drivers[driverId];
          const currentShift = this.getActiveShift(driverData);
          
          if (process.env.NODE_ENV === 'development' && vehicleType === 2 && minute === 0) {
            console.log(`- Fahrzeug ${vehicle.id}: Aktive Schicht gefunden: ${!!currentShift}`);
            if (currentShift) {
              console.log(`- Schicht aktiv: ${currentShift.isActive}`);
              console.log(`- Schicht Start: ${currentShift.shiftStart?.toDate()}`);
              console.log(`- Schicht Ende: ${currentShift.shiftEnd?.toDate()}`);
            }
          }
          
          if (!currentShift) return;
          
          // Schichtzeiten prüfen
          const shiftStart = currentShift.shiftStart?.toDate();
          const shiftEnd = currentShift.shiftEnd?.toDate();
          const isShiftActive = currentShift.isActive;
          
          if (!isShiftActive || !shiftStart || checkTime < shiftStart) {
            if (process.env.NODE_ENV === 'development' && vehicleType === 2 && minute === 0) {
              console.log(`- Fahrzeug ${vehicle.id}: Schicht nicht aktiv oder noch nicht gestartet`);
            }
            return;
          }
          if (shiftEnd && checkTime > shiftEnd) {
            if (process.env.NODE_ENV === 'development' && vehicleType === 2 && minute === 0) {
              console.log(`- Fahrzeug ${vehicle.id}: Schicht bereits beendet`);
            }
            return;
          }
          
          totalWithDriver++;
          
          // Pausenprüfung
          const isInBreak = currentShift.breaks && currentShift.breaks.some(breakItem => {
            if (!breakItem.startTime) return false;
            const breakStart = breakItem.startTime.toDate();
            const breakEnd = breakItem.endTime ? breakItem.endTime.toDate() : 
              new Date(breakStart.getTime() + ((breakItem.plannedDuration || 30) * 60000));
            return breakStart <= checkTime && breakEnd > checkTime;
          });
          
          // Fahrtprüfung
          const isOnRide = this.activities.some(activity => {
            if (activity.type !== 'ride') return false;
            
            // Prüfe sowohl echte vehicleId als auch virtuelle IDs für den Fahrer
            const isVehicleMatch = activity.vehicleId === vehicle.id || 
                                 activity.vehicleId === `virtual-${driverId}` ||
                                 activity.vehicleId === `virtual-ride-${driverId}` ||
                                 activity.vehicleId === `virtual-scheduled-${driverId}`;
            
            if (!isVehicleMatch) return false;
            
            const blockingStatuses = ['pre_accepted', 'accepted', 'arrived', 'in_progress'];
            if (!blockingStatuses.includes(activity.status)) return false;
            
            const effectiveStartTime = (activity.status === 'pre_accepted' && activity.hasApproachTime) ? 
              activity.approachStartTime : activity.startTime;
            
            return effectiveStartTime <= checkTime && activity.endTime > checkTime;
          });
          
          if (process.env.NODE_ENV === 'development' && vehicleType === 2 && minute === 0) {
            console.log(`- Fahrzeug ${vehicle.id}: In Pause: ${isInBreak}, Auf Fahrt: ${isOnRide}`);
          }
          
          if (!isInBreak && !isOnRide) {
            availableCount++;
            if (process.env.NODE_ENV === 'development' && vehicleType === 2 && minute === 0) {
              console.log(`- Fahrzeug ${vehicle.id}: ✅ VERFÜGBAR (availableCount jetzt: ${availableCount})`);
            }
          }
        } else {
          if (process.env.NODE_ENV === 'development' && vehicleType === 2 && minute === 0) {
            console.log(`- Fahrzeug ${vehicle.id}: ❌ Keine Schichtdaten für Fahrer ${driverId} gefunden`);
          }
        }
      });
      
      timeline.push({
        minute,
        time: checkTime,
        availableCount,
        totalWithDriver
      });
    }
    
    // Segment-Gruppierung für Fahrzeugtyp
    const segments = [];
    let currentSegment = null;
    
    timeline.forEach((point) => {
      const status = point.availableCount > 0 ? 'normal' : 'unavailable';
      
      if (!currentSegment || currentSegment.status !== status) {
        if (currentSegment) {
          segments.push(currentSegment);
        }
        currentSegment = {
          startMinute: point.minute,
          endMinute: point.minute + 1,
          availableCount: point.availableCount,
          totalWithDriver: point.totalWithDriver,
          status: status,
          startTime: point.time,
          endTime: new Date(point.time.getTime() + 60000)
        };
      } else {
        currentSegment.endMinute = point.minute + 1;
        currentSegment.endTime = new Date(point.time.getTime() + 60000);
        currentSegment.availableCount = point.availableCount;
        currentSegment.totalWithDriver = point.totalWithDriver;
      }
    });
    
    if (currentSegment) {
      segments.push(currentSegment);
    }
    
    return { segments };
  }

  // Aktuelle Wartezeiten für alle Fahrzeugtypen berechnen
  calculateCurrentWaitTime() {
    if (!this.shiftsData || !this.shiftsData.drivers) {
      return { general: "Keine Fahrer verfügbar", grossraum: "Keine Fahrer verfügbar", kleinbus7: "Keine Fahrer verfügbar", kleinbus8: "Keine Fahrer verfügbar" };
    }

    const now = new Date();
    const availabilityTimeline = this.buildAvailabilityTimeline();
    
    // Finde aktuelles Segment
    const currentSegment = availabilityTimeline.segments.find(segment => 
      now >= segment.startTime && now < segment.endTime
    );

    // Berechne für Standard-Fahrzeuge
    const currentAvailable = currentSegment ? currentSegment.availableCount : 0;
    const generalWaitTime = this.calculateWaitTimeFromTimeline(currentAvailable);
    
    // Prüfe ob überhaupt Fahrer online sind
    // Wenn generalWaitTime eine konkrete Zeit zeigt (nicht "Keine Fahrer verfügbar"), sind Fahrer online
    const hasAnyOnlineDriver = generalWaitTime !== "Keine Fahrer verfügbar";

    // Prüfe erst ob Fahrzeuge des Typs existieren und mit Fahrern besetzt sind
    const availableVehicleTypes = this.vehicles.reduce((acc, vehicle) => {
      if (vehicle.current_driver && vehicle.vehicleType) {
        if (vehicle.vehicleType === 2 || vehicle.vehicleType === 3 || vehicle.vehicleType === 4) acc.grossraum = true;
        if (vehicle.vehicleType === 3 || vehicle.vehicleType === 4) acc.kleinbus7 = true;
        if (vehicle.vehicleType === 4) acc.kleinbus8 = true;
      }
      return acc;
    }, { grossraum: false, kleinbus7: false, kleinbus8: false });
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`WaitTimeManager: 🔍 KOMBI DEBUG - Vehicle Type Prüfung:`);
      console.log('- Alle Fahrzeuge:', this.vehicles.length);
      this.vehicles.forEach(v => {
        console.log(`  - Fahrzeug ${v.id}: Typ ${v.vehicleType}, Fahrer: ${v.current_driver?.id || 'keiner'}`);
      });
      console.log('- Verfügbare Fahrzeugtypen:', availableVehicleTypes);
    }

    // Berechne für Großraum (Typ 2) - nur wenn Fahrzeuge verfügbar
    let grossraumWaitTime = hasAnyOnlineDriver ? "nicht verfügbar" : "Keine Fahrer verfügbar";
    if (availableVehicleTypes.grossraum) {
      const grossraumAvailability = this.calculateVehicleTypeAvailability(2);
      const grossraumSegments = grossraumAvailability.segments;
      const currentGrossraumSegment = grossraumSegments.find(segment =>
        now >= segment.startTime && now < segment.endTime
      );
      grossraumWaitTime = this.calculateWaitTimeFromTimeline(
        currentGrossraumSegment ? currentGrossraumSegment.availableCount : 0,
        grossraumSegments
      );
    }

    // Berechne für Kleinbus 7 (Typ 3) - nur wenn Fahrzeuge verfügbar
    let kleinbus7WaitTime = hasAnyOnlineDriver ? "nicht verfügbar" : "Keine Fahrer verfügbar";
    if (availableVehicleTypes.kleinbus7) {
      const kleinbus7Availability = this.calculateVehicleTypeAvailability(3);
      const kleinbus7Segments = kleinbus7Availability.segments;
      const currentKleinbus7Segment = kleinbus7Segments.find(segment =>
        now >= segment.startTime && now < segment.endTime
      );
      kleinbus7WaitTime = this.calculateWaitTimeFromTimeline(
        currentKleinbus7Segment ? currentKleinbus7Segment.availableCount : 0,
        kleinbus7Segments
      );
    }

    // Berechne für Kleinbus 8 (Typ 4) - nur wenn Fahrzeuge verfügbar
    let kleinbus8WaitTime = hasAnyOnlineDriver ? "nicht verfügbar" : "Keine Fahrer verfügbar";
    if (availableVehicleTypes.kleinbus8) {
      const kleinbus8Availability = this.calculateVehicleTypeAvailability(4);
      const kleinbus8Segments = kleinbus8Availability.segments;
      const currentKleinbus8Segment = kleinbus8Segments.find(segment =>
        now >= segment.startTime && now < segment.endTime
      );
      kleinbus8WaitTime = this.calculateWaitTimeFromTimeline(
        currentKleinbus8Segment ? currentKleinbus8Segment.availableCount : 0,
        kleinbus8Segments
      );
    }

    return {
      general: generalWaitTime,
      grossraum: grossraumWaitTime,
      kleinbus7: kleinbus7WaitTime,
      kleinbus8: kleinbus8WaitTime
    };
  }

  // Firebase Shifts Listener einrichten (wie im ShiftCalendar)
  async setupShiftsListener() {
    try {
      const formatDateString = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      const currentDateString = formatDateString(this.currentDate);
      const previousDate = new Date(this.currentDate);
      previousDate.setDate(previousDate.getDate() - 1);
      const previousDateString = formatDateString(previousDate);
      
      console.log('WaitTimeManager: Setup Shifts Listener für:', currentDateString, 'und', previousDateString);
      
      const currentShiftsDocRef = doc(db, 'shifts', currentDateString);
      const previousShiftsDocRef = doc(db, 'shifts', previousDateString);
      
      let currentShiftsData = null;
      let previousShiftsData = null;
      
      const updateCombinedShiftsData = () => {
        const combinedData = { drivers: {} };
        
        // Zuerst vorherige Schichten laden (für rollierende Ansicht)
        if (previousShiftsData && previousShiftsData.drivers) {
          Object.entries(previousShiftsData.drivers).forEach(([driverId, driverData]) => {
            combinedData.drivers[driverId] = { ...driverData, _fromPreviousDay: true };
          });
        }
        
        // Dann aktuelle Schichten (überschreibt bei Konflikten)
        if (currentShiftsData && currentShiftsData.drivers) {
          Object.entries(currentShiftsData.drivers).forEach(([driverId, driverData]) => {
            combinedData.drivers[driverId] = { ...driverData, _fromCurrentDay: true };
          });
        }
        
        this.shiftsData = combinedData;
        
        // Berechnung triggern wenn aktiv
        if (this.isActive) {
          this.performCalculation();
        }
        
        console.log('WaitTimeManager: Shifts Data aktualisiert:', Object.keys(combinedData.drivers).length, 'Fahrer');
      };
      
      // Listener für aktuelle Schichten
      const unsubscribeCurrentShifts = onSnapshot(currentShiftsDocRef, (docSnapshot) => {
        currentShiftsData = docSnapshot.exists() ? docSnapshot.data() : null;
        updateCombinedShiftsData();
      });
      
      // Listener für vorherige Schichten
      const unsubscribePreviousShifts = onSnapshot(previousShiftsDocRef, (docSnapshot) => {
        previousShiftsData = docSnapshot.exists() ? docSnapshot.data() : null;
        updateCombinedShiftsData();
      });
      
      // Cleanup-Funktion für beide Listener
      this.shiftsListener = () => {
        unsubscribeCurrentShifts();
        unsubscribePreviousShifts();
      };
      
    } catch (error) {
      console.error('WaitTimeManager: Fehler beim Setup des Shifts Listeners:', error);
    }
  }

  // Initiale Schichtdaten laden
  async loadShiftsData() {
    try {
      const formatDateString = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      const currentDateString = formatDateString(this.currentDate);
      const previousDate = new Date(this.currentDate);
      previousDate.setDate(previousDate.getDate() - 1);
      const previousDateString = formatDateString(previousDate);
      
      console.log('WaitTimeManager: Lade initiale Shift-Daten für:', currentDateString, 'und', previousDateString);
      
      const currentShiftsDocRef = doc(db, 'shifts', currentDateString);
      const previousShiftsDocRef = doc(db, 'shifts', previousDateString);
      
      const [currentDoc, previousDoc] = await Promise.all([
        getDoc(currentShiftsDocRef),
        getDoc(previousShiftsDocRef)
      ]);
      
      // Kombiniere Shifts-Daten
      const combinedData = { drivers: {} };
      
      // Zuerst vorherige Schichten
      if (previousDoc.exists() && previousDoc.data().drivers) {
        Object.entries(previousDoc.data().drivers).forEach(([driverId, driverData]) => {
          combinedData.drivers[driverId] = { ...driverData, _fromPreviousDay: true };
        });
      }
      
      // Dann aktuelle Schichten
      if (currentDoc.exists() && currentDoc.data().drivers) {
        Object.entries(currentDoc.data().drivers).forEach(([driverId, driverData]) => {
          combinedData.drivers[driverId] = { ...driverData, _fromCurrentDay: true };
        });
      }
      
      this.shiftsData = combinedData;
      console.log('WaitTimeManager: Initiale Shift-Daten geladen:', Object.keys(combinedData.drivers).length, 'Fahrer');
      
      if (process.env.NODE_ENV === 'development') {
        console.log('WaitTimeManager: Firebase Shifts-Daten im Detail:');
        Object.entries(combinedData.drivers).forEach(([driverId, driverData]) => {
          const activeShift = this.getActiveShift(driverData);
          if (activeShift) {
            console.log(`- Firebase Fahrer ${driverId}: ${activeShift.breaks?.length || 0} Pausen, Schicht aktiv: ${activeShift.isActive}`);
            if (activeShift.breaks && activeShift.breaks.length > 0) {
              activeShift.breaks.forEach((br, idx) => {
                const start = br.startTime?.toDate?.() || br.startTime;
                const end = br.endTime?.toDate?.() || br.endTime;
                console.log(`  - Pause ${idx}: ${start?.toLocaleTimeString()} - ${end?.toLocaleTimeString() || 'laufend'}`);
              });
            }
          }
        });
      }
      
    } catch (error) {
      console.error('WaitTimeManager: Fehler beim Laden der initialen Shift-Daten:', error);
    }
  }

  // Daten von Redux Store aktualisieren (ersetzt updateData)
  updateFromReduxStore(shiftsData, activities, vehicles, currentDate = new Date()) {
    // Aktualisiere nur wenn Daten verfügbar sind
    if (shiftsData && activities && vehicles) {
      this.shiftsData = shiftsData;
      this.activities = activities;
      this.vehicles = vehicles;
      this.currentDate = currentDate;
      this.dataSource = 'appDataManager'; // Neue einheitliche Datenquelle
      
      if (process.env.NODE_ENV === 'development') {
        console.log('WaitTimeManager: ✅ AppDataManager-Daten empfangen:', Object.keys(shiftsData.drivers || {}).length, 'Fahrer');
        console.log('WaitTimeManager: Activities:', activities.length, 'Vehicles:', vehicles.length);
      }
      
      // Neue Berechnung triggern wenn Service aktiv ist
      if (this.isActive) {
        this.performCalculation();
      }
    }
  }

  // Wartezeitberechnung durchführen
  async performCalculation() {
    try {
      // Prüfe ob vollständige AppDataManager-Daten verfügbar sind
      if (!this.dataSource || this.dataSource !== 'appDataManager') {
        if (process.env.NODE_ENV === 'development') {
          console.log('WaitTimeManager: ⏳ Warte auf AppDataManager-Daten via Redux...');
          console.log('WaitTimeManager: Aktuelle Datenquelle:', this.dataSource || 'keine');
        }
        
        // Setze Wartezeiten auf "Berechnung..." bis AppDataManager-Daten verfügbar sind
        this.currentWaitTimes = {
          general: "Berechnung...",
          grossraum: "Berechnung...",
          kleinbus7: "Berechnung...",
          kleinbus8: "Berechnung..."
        };
        this.nextUnavailable = null;
        this.notifyUpdateCallbacks();
        return;
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log('WaitTimeManager: ✅ Starte Wartezeitberechnung mit AppDataManager-Daten...');
        console.log('- Verfügbare Daten: Shifts:', Object.keys(this.shiftsData?.drivers || {}).length, 'Activities:', this.activities.length, 'Vehicles:', this.vehicles.length);
      }
      
      const waitTimes = this.calculateCurrentWaitTime();
      const nextUnavailable = this.calculateTimeToNextUnavailability();
      
      // Prüfe ob sich die Wartezeiten tatsächlich geändert haben
      const waitTimesChanged = !this.currentWaitTimes || 
        this.currentWaitTimes.general !== waitTimes.general ||
        this.currentWaitTimes.grossraum !== waitTimes.grossraum ||
        this.currentWaitTimes.kleinbus7 !== waitTimes.kleinbus7 ||
        this.currentWaitTimes.kleinbus8 !== waitTimes.kleinbus8;
      
      const nextUnavailableChanged = JSON.stringify(this.nextUnavailable) !== JSON.stringify(nextUnavailable);
      
      // Nur bei tatsächlichen Änderungen fortfahren
      if (!waitTimesChanged && !nextUnavailableChanged) {
        if (process.env.NODE_ENV === 'development') {
          console.log('WaitTimeManager: ⏭️ Berechnung übersprungen - keine Änderungen');
        }
        return;
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log('WaitTimeManager: ✅ Berechnete Wartezeiten:', waitTimes);
        if (nextUnavailable) {
          console.log('WaitTimeManager: Nächste Nichtverfügbarkeit in', nextUnavailable.minutesUntil, 'Minuten');
        } else {
          console.log('WaitTimeManager: Keine Nichtverfügbarkeit gefunden');
        }
      }
      
      this.currentWaitTimes = waitTimes;
      this.nextUnavailable = nextUnavailable;
      this.lastCalculationTime = new Date();
      
      // Metadaten erstellen
      this.waitTimeMetadata = {
        calculatedAt: this.lastCalculationTime,
        calculatedBy: 'webApp',
        isWebAppActive: true
      };
      
      // Firebase aktualisieren
      await this.updateFirebaseWaitTimes(waitTimes, nextUnavailable, this.waitTimeMetadata);
      
      // UI-Updates triggern
      this.notifyUpdateCallbacks();
      
      if (process.env.NODE_ENV === 'development') {
        console.log('WaitTimeManager: Berechnung abgeschlossen:', waitTimes);
      }
      
    } catch (error) {
      console.error('WaitTimeManager: Fehler bei Wartezeitberechnung:', error);
    }
  }

  // Firebase Wartezeiten aktualisieren - nur bei Änderungen
  async updateFirebaseWaitTimes(waitTimes, nextUnavailable, metadata) {
    try {
      // Prüfe ob sich die Wartezeiten geändert haben
      const waitTimesChanged = !this.previousWaitTimes || 
        this.previousWaitTimes.general !== waitTimes.general ||
        this.previousWaitTimes.grossraum !== waitTimes.grossraum ||
        this.previousWaitTimes.kleinbus7 !== waitTimes.kleinbus7 ||
        this.previousWaitTimes.kleinbus8 !== waitTimes.kleinbus8;
      
      // Prüfe ob sich nextUnavailable geändert hat
      const nextUnavailableChanged = JSON.stringify(this.previousNextUnavailable) !== JSON.stringify(nextUnavailable);
      
      // Nur updaten wenn sich etwas geändert hat
      if (waitTimesChanged || nextUnavailableChanged) {
        const waitTimeDocRef = doc(db, 'waitTimes', 'current');
        
        await setDoc(waitTimeDocRef, {
          ...waitTimes,
          nextUnavailable,
          calculatedBy: 'webApp',
          metadata,
          lastUpdated: new Date()
        });
        
        // Speichere aktuelle Werte für nächsten Vergleich
        this.previousWaitTimes = { ...waitTimes };
        this.previousNextUnavailable = nextUnavailable ? { ...nextUnavailable } : null;
        
        if (process.env.NODE_ENV === 'development') {
          console.log('WaitTimeManager: 🔄 Firebase Update durchgeführt - Wartezeiten haben sich geändert');
          if (waitTimesChanged && this.previousWaitTimes) {
            console.log('- Wartezeiten: Alt:', this.previousWaitTimes, 'Neu:', waitTimes);
          }
          if (nextUnavailableChanged) {
            console.log('- Nächste Nichtverfügbarkeit geändert');
          }
        }
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log('WaitTimeManager: ⏭️ Firebase Update übersprungen - keine Änderungen');
        }
      }
      
    } catch (error) {
      console.error('WaitTimeManager: Fehler beim Firebase-Update:', error);
    }
  }

  // App-Status in Firebase setzen
  async setAppStatus(isActive) {
    try {
      const statusDocRef = doc(db, 'appStatus', 'webApp');
      
      await setDoc(statusDocRef, {
        isActive,
        lastSeen: new Date(),
        calculatingWaitTimes: isActive
      });
      
    } catch (error) {
      console.error('WaitTimeManager: Fehler beim App-Status-Update:', error);
    }
  }

  // Service starten
  async start() {
    if (this.isActive) {
      console.warn('WaitTimeManager: Service bereits aktiv');
      return;
    }
    
    this.isActive = true;
    
    try {
      // App-Status in Firebase setzen
      await this.setAppStatus(true);
      
      // Daten kommen jetzt von AppDataManager via Redux
      console.log('WaitTimeManager: Warte auf AppDataManager-Daten via Redux Store...');
      console.log('WaitTimeManager: Berechnungen werden von Redux-Updates getriggert');
      
      // Initiale Berechnung (falls Daten bereits verfügbar)
      this.performCalculation();
      
      // Regelmäßige Updates alle 2 Minuten (Fallback)
      this.updateInterval = setInterval(() => {
        this.performCalculation();
      }, 120000);
      
      console.log('WaitTimeManager: Service gestartet (AppDataManager hat Priorität)');
      
    } catch (error) {
      console.error('WaitTimeManager: Fehler beim Service-Start:', error);
      this.isActive = false;
    }
  }

  // Service stoppen
  async stop() {
    if (!this.isActive) {
      return;
    }
    
    this.isActive = false;
    
    try {
      // Update-Interval stoppen
      if (this.updateInterval) {
        clearInterval(this.updateInterval);
        this.updateInterval = null;
      }
      
      // Firebase Listener stoppen
      if (this.firebaseListener) {
        this.firebaseListener();
        this.firebaseListener = null;
      }
      
      // Shifts Listener stoppen (falls jemals verwendet)
      if (this.shiftsListener) {
        this.shiftsListener();
        this.shiftsListener = null;
      }
      
      // App-Status in Firebase deaktivieren
      await this.setAppStatus(false);
      
      console.log('WaitTimeManager: Service gestoppt');
      
    } catch (error) {
      console.error('WaitTimeManager: Fehler beim Service-Stopp:', error);
    }
  }

  // Update-Callback registrieren
  registerUpdateCallback(callback) {
    this.updateCallbacks.add(callback);
    
    // Cleanup-Funktion zurückgeben
    return () => {
      this.updateCallbacks.delete(callback);
    };
  }

  // Alle Update-Callbacks benachrichtigen
  notifyUpdateCallbacks() {
    this.updateCallbacks.forEach(callback => {
      try {
        callback({
          waitTimes: this.currentWaitTimes,
          nextUnavailable: this.nextUnavailable,
          metadata: this.waitTimeMetadata
        });
      } catch (error) {
        console.error('WaitTimeManager: Fehler bei Update-Callback:', error);
      }
    });
  }

  // Aktuelle Wartezeiten abrufen
  getCurrentWaitTimes() {
    return {
      waitTimes: this.currentWaitTimes,
      nextUnavailable: this.nextUnavailable,
      metadata: this.waitTimeMetadata
    };
  }

  // Service-Status prüfen
  getStatus() {
    return {
      isActive: this.isActive,
      lastCalculationTime: this.lastCalculationTime,
      hasData: this.shiftsData !== null && this.shiftsData.drivers && Object.keys(this.shiftsData.drivers).length > 0,
      dataCount: {
        drivers: this.shiftsData?.drivers ? Object.keys(this.shiftsData.drivers).length : 0,
        activities: this.activities.length,
        vehicles: this.vehicles.length
      },
      hasShiftsListener: this.shiftsListener !== null,
      shiftsDataStructure: this.shiftsData ? Object.keys(this.shiftsData) : null
    };
  }
}

// Singleton-Instanz erstellen
const waitTimeManager = new WaitTimeManager();

export default waitTimeManager;